import React, { useState, useEffect } from 'react';
import { 
  Copy, 
  Edit2
} from 'lucide-react';
import axios from '../../services/api';
import { referralService } from '../../services/referralService';
import { useI18n } from '../../contexts/I18nContext';

const MyInvites = () => {
  const { t } = useI18n();
  const [referralData, setReferralData] = useState({
    referralCode: 'NOT SET',
    referralLink: '',
    stats: {
      totalReferrals: 0,
      monthlyReferrals: 0,
      registrationRewards: 0,
      totalPurchaseRewards: 0
    },
    invitations: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copyLinkSuccess, setCopyLinkSuccess] = useState(false);
  const [editingCode, setEditingCode] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [referralBonus, setReferralBonus] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch system config for referral bonus
       try {
         const configResponse = await axios.get('/system/config/registration-bonus');
         if (configResponse.data.success && configResponse.data.data) {
           setReferralBonus(configResponse.data.data.referralBonus);
         }
       } catch (configErr) {
        console.error('Failed to fetch system config:', configErr);
        // Continue loading other data even if config fetch fails
      }

      const [linkData, statsData, invitationsData] = await Promise.all([
        referralService.getReferralLink(),
        referralService.getReferralStats(),
        referralService.getReferredUsers()
      ]);
      
      // 生成完整的邀请链接
      const baseUrl = window.location.origin;
      const referralLink = linkData.referralCode && linkData.referralCode !== 'NOT SET' 
        ? `${baseUrl}/login?ref=${linkData.referralCode}`
        : '';
      
      setReferralData({
        referralCode: linkData.referralCode || 'NOT SET',
        referralLink: referralLink,
        stats: {
          totalReferrals: statsData.totalReferrals || 0,
          monthlyReferrals: statsData.monthlyReferrals || 0,
          registrationRewards: statsData.registrationRewards || 0,
          totalPurchaseRewards: statsData.totalPurchaseRewards || 0
        },
        invitations: invitationsData.referredUsers || []
      });
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // const copyInvitationLink = async () => {
  //   try {
  //     await navigator.clipboard.writeText(referralData.referralLink);
  //     setCopyLinkSuccess(true);
  //     setTimeout(() => setCopyLinkSuccess(false), 2000);
  //   } catch (err) {
  //     console.error('Copy invitation link failed:', err);
  //   }
  // };

  const copyInvitationLink = async () => {
    const link = referralData.referralLink;

    // 检查现代 Clipboard API 是否可用
    if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
            await navigator.clipboard.writeText(link);
            setCopyLinkSuccess(true);
            setTimeout(() => setCopyLinkSuccess(false), 2000);
        } catch (err) {
            console.error('Copy failed with modern API (Permission denied or other error):', err);
            // 失败时尝试回退方法
            fallbackCopy(link);
        }
    } else {
        // 如果 API 不可用（即在 HTTP 环境下），直接使用回退方法
        console.warn('Clipboard API not available. Using fallback method.');
        fallbackCopy(link);
    }
};

// 回退方法：使用已被废弃但兼容性强的 document.execCommand
function fallbackCopy(text) {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    // 隐藏元素但不影响选择
    textArea.style.position = "fixed"; 
    textArea.style.left = "-9999px"; 
    document.body.appendChild(textArea);
    textArea.select();

    try {
        const successful = document.execCommand('copy');
        if (successful) {
            setCopyLinkSuccess(true);
            setTimeout(() => setCopyLinkSuccess(false), 2000);
        } else {
            console.error('Fallback copy failed.');
        }
    } catch (err) {
        console.error('Fallback copy failed:', err);
    }

    document.body.removeChild(textArea);
}

  const handleEditCode = () => {
    setEditingCode(true);
    setNewCode(referralData.referralCode === 'NOT SET' ? '' : referralData.referralCode);
  };

  const handleSaveCode = async () => {
    try {
      // 这里应该调用API保存邀请码
      // await referralService.updateReferralCode(newCode);
      
      // 更新邀请码和邀请链接
      const baseUrl = window.location.origin;
      const referralLink = newCode && newCode !== 'NOT SET' 
        ? `${baseUrl}/login?ref=${newCode}`
        : '';
      
      setReferralData(prev => ({
        ...prev,
        referralCode: newCode || 'NOT SET',
        referralLink: referralLink
      }));
      setEditingCode(false);
    } catch (err) {
      setError('Failed to save invitation code: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={loadData} className="retry-button">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="my-invites">
      {/* Page Title */}
      <div className="page-header">
        <h1>{t('personalCenter.myInvites')}</h1>
      </div>

      {/* Invitation Code Setting */}
      <div className="card">
        <div className="card-header">
          <h3>{t('invites.invitationCode')}</h3>
        </div>
        <div className="card-content">
          <div className="invitation-code-section">
            <div className="code-display">
              {editingCode ? (
                <div className="code-edit">
                  <input
                    type="text"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                    placeholder={t('invites.enterInvitationCode')}
                    className="code-input"
                  />
                  <div className="code-actions">
                    <button onClick={handleSaveCode} className="btn btn-primary">
                      {t('common.save')}
                    </button>
                    <button onClick={() => setEditingCode(false)} className="btn btn-secondary">
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="code-view">
                  <div className="code-display-row">
                    <span className="code-text">{referralData.referralCode}</span>
                    <div className="button-group">
                      <button onClick={handleEditCode} className="btn btn-outline">
                        <Edit2 size={16} />
                      </button>
                      <button 
                        onClick={copyInvitationLink}
                        className="btn btn-primary"
                      >
                        <Copy size={16} />
                        {copyLinkSuccess ? t('invites.copied') : t('invites.copyInvitationLink')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {referralBonus && (
              <p className="code-description">
                {t('invites.inviteRewardDescription', { amount: referralBonus })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Invitation Reward Balance */}
      <div className="card">
        <div className="card-header">
          <h3>{t('invites.invitationRewardBalance')}</h3>
        </div>
        <div className="card-content">
          <div className="reward-balance">
            <div className="balance-amount">
              {(referralData.stats.registrationRewards + referralData.stats.totalPurchaseRewards)} {t('credits.credits')}
            </div>
          </div>
        </div>
      </div>

      {/* Invitation Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{referralData.stats.totalReferrals}</div>
          <div className="stat-label">{t('invites.totalInvitationCount')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{referralData.stats.monthlyReferrals}</div>
          <div className="stat-label">{t('invites.monthlyReferrals')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{referralData.stats.registrationRewards} {t('credits.credits')}</div>
          <div className="stat-label">{t('invites.registrationReward')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{referralData.stats.totalPurchaseRewards} {t('credits.credits')}</div>
          <div className="stat-label">{t('invites.invitationReward')}</div>
        </div>
      </div>

      {/* Invitation Records Table */}
      <div className="table-container">
        <div className="table-header">
          <h3>{t('invites.invitationRecords')}</h3>
        </div>
        
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>{t('invites.invitationTime')}</th>
                <th>{t('invites.invitedUser')}</th>
                <th>{t('invites.email')}</th>
                <th>{t('invites.rewardCredits')}</th>
              </tr>
            </thead>
            <tbody>
              {referralData.invitations.length > 0 ? (
                referralData.invitations.map((invitation, index) => (
                  <tr key={invitation.id || index}>
                    <td>{formatDate(invitation.referredAt)}</td>
                    <td>{invitation.name}</td>
                    <td>{invitation.email || '-'}</td>
                    <td>{invitation.registrationRewardGiven || 0} {t('credits.credits')}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="no-data">
                    {t('invites.noInvitationRecords')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyInvites;
